import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, API, apiError } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Plus, Trash2, Loader2, ImageUp, ArrowUp, ArrowDown, LayoutList, ExternalLink, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const PAGINE_EVENT = "sito-pagine-changed";

export const usePagine = () => {
  const [pagine, setPagine] = useState([]);
  const load = useCallback(() => { api.get("/public/sito/pagine").then((r) => { setPagine(r.data || []); }).catch(() => {}); }, []);
  useEffect(() => {
    load();
    window.addEventListener(PAGINE_EVENT, load);
    return () => { window.removeEventListener(PAGINE_EVENT, load); };
  }, [load]);
  return { pagine, reload: () => window.dispatchEvent(new Event(PAGINE_EVENT)) };
};

const useSezioni = (pagina, edit) => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => {
    if (!pagina) { return; }
    api.get(edit ? "/sito/sezioni" : "/public/sito/sezioni", { params: { pagina } }).then((r) => { setItems(r.data || []); }).catch(() => {});
  }, [pagina, edit]);
  useEffect(() => { load(); }, [load]);
  return { items, reload: load };
};

const sezImg = (s) => (s.has_image ? `${API}/public/sito/sezioni/${s.id}/image?v=${encodeURIComponent(s.image_v || "")}` : null);
const EMPTY = { sopratitolo: "", titolo: "", testo: "", stile: "chiaro", voci: [], pulsante_testo: "", pulsante_link: "", pubblicata: false };

const VociField = ({ value, onChange }) => (
  <div className="space-y-2">
    {value.map((v, i) => (
      <div key={i} className="flex gap-2 rounded-md border border-slate-200 p-2">
        <div className="flex-1 space-y-1">
          <Input value={v.t} placeholder="Titolo del riquadro" onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, t: e.target.value } : x)))} data-testid={`sezione-voce-t-${i}`} />
          <Textarea rows={2} value={v.d} placeholder="Descrizione" onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, d: e.target.value } : x)))} data-testid={`sezione-voce-d-${i}`} />
        </div>
        <Button type="button" size="icon" variant="ghost" className="text-red-700" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`sezione-voce-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    ))}
    <Button type="button" size="sm" variant="outline" onClick={() => onChange([...value, { t: "", d: "" }])} data-testid="sezione-voce-add"><Plus className="mr-1 h-4 w-4" /> Aggiungi riquadro</Button>
  </div>
);

const ImageField = ({ preview, onPick, onRemove }) => (
  <div className="space-y-2">
    <Label className="text-xs">Foto</Label>
    {preview && <img src={preview} alt="Anteprima" className="max-h-40 rounded-md object-cover" data-testid="sezione-preview" />}
    <div className="flex flex-wrap gap-2">
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm font-medium hover:bg-slate-200">
        <ImageUp className="h-4 w-4" /> {preview ? "Cambia foto" : "Aggiungi foto"}
        <input type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="sezione-image-input" />
      </label>
      {preview && <Button type="button" variant="ghost" size="sm" className="text-red-700" onClick={onRemove} data-testid="sezione-image-remove">Togli foto</Button>}
    </div>
  </div>
);

const SezioneDialog = ({ sezione, pagina, onClose, onSaved }) => {
  const [d, setD] = useState(sezione ? { ...EMPTY, ...sezione, pubblicata: sezione.pubblicata !== false } : EMPTY);
  const [image, setImage] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [busy, setBusy] = useState(false);
  const preview = image || (!removeImage && sezione ? sezImg(sezione) : null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) { setImage(await resizeImage(f, 1600, 1600, 0.82)); setRemoveImage(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const body = { pagina, sopratitolo: d.sopratitolo, titolo: d.titolo, testo: d.testo, stile: d.stile, voci: d.voci, pulsante_testo: d.pulsante_testo, pulsante_link: d.pulsante_link, pubblicata: d.pubblicata, image, remove_image: removeImage };
      if (sezione) { await api.put(`/sito/sezioni/${sezione.id}`, body); } else { await api.post("/sito/sezioni", body); }
      toast.success(d.pubblicata ? "Sezione pubblicata" : "Bozza salvata: la vedono solo admin e coordinatori");
      onSaved();
      onClose();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="sezione-dialog">
        <DialogHeader><DialogTitle>{sezione ? "Modifica sezione" : "Nuova sezione"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-amber-50 p-1 ring-1 ring-amber-200">
            {[[false, "Bozza (solo chi gestisce il sito)"], [true, "Pubblicata (visibile a tutti)"]].map(([k, l]) => (
              <button key={String(k)} type="button" onClick={() => setD({ ...d, pubblicata: k })} data-testid={`sezione-stato-${k ? "pubblicata" : "bozza"}`}
                className={`rounded-md py-2 text-xs font-semibold ${d.pubblicata === k ? "bg-white text-[#14382C] shadow-sm" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-1">
            {[["chiaro", "Sfondo chiaro"], ["scuro", "Sfondo verde"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setD({ ...d, stile: k })} data-testid={`sezione-stile-${k}`}
                className={`rounded-md py-2 text-xs font-semibold ${d.stile === k ? "bg-white text-[#14382C] shadow-sm" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1"><Label className="text-xs">Sopratitolo</Label><Input value={d.sopratitolo} onChange={set("sopratitolo")} data-testid="sezione-sopratitolo" /></div>
            <div className="space-y-1"><Label className="text-xs">Titolo</Label><Input value={d.titolo} onChange={set("titolo")} data-testid="sezione-titolo" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Testo</Label><Textarea rows={6} value={d.testo} onChange={set("testo")} data-testid="sezione-testo" /></div>
          <ImageField preview={preview} onPick={pick} onRemove={() => { setImage(null); setRemoveImage(true); }} />
          <div className="space-y-1"><Label className="text-xs">Riquadri (facoltativi)</Label><VociField value={d.voci} onChange={(voci) => setD({ ...d, voci })} /></div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1"><Label className="text-xs">Testo del pulsante (facoltativo)</Label><Input value={d.pulsante_testo} onChange={set("pulsante_testo")} data-testid="sezione-pulsante-testo" /></div>
            <div className="space-y-1"><Label className="text-xs">Link del pulsante</Label><Input value={d.pulsante_link} placeholder="https://..." onChange={set("pulsante_link")} data-testid="sezione-pulsante-link" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy} className="bg-[#14382C] hover:bg-[#0E281F]" data-testid="sezione-save">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SezioneTools = ({ s, i, n, onEdit, onMove, onDelete, onPublish }) => {
  const b = "inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#14382C] shadow-lg transition-transform hover:scale-105 disabled:opacity-40";
  return (
    <div className="absolute right-4 top-4 z-20 flex flex-wrap justify-end gap-1.5">
      <button type="button" onClick={onPublish} data-testid={`sezione-publish-${i}`}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-lg ${s.pubblicata === false ? "bg-[#166534] text-white" : "bg-white text-slate-700"}`}>
        {s.pubblicata === false ? <><Eye className="h-3.5 w-3.5" /> Pubblica</> : <><EyeOff className="h-3.5 w-3.5" /> Nascondi</>}
      </button>
      <button type="button" className={b} disabled={i === 0} onClick={() => onMove(-1)} aria-label="Sposta su" data-testid={`sezione-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
      <button type="button" className={b} disabled={i === n - 1} onClick={() => onMove(1)} aria-label="Sposta giù" data-testid={`sezione-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
      <button type="button" onClick={onEdit} data-testid={`sezione-edit-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#D97706] px-3 py-1.5 text-xs font-semibold text-white shadow-lg"><Pencil className="h-3.5 w-3.5" /> Modifica</button>
      <button type="button" className={`${b} text-red-700`} onClick={onDelete} aria-label="Elimina" data-testid={`sezione-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
};

const SezioneBody = ({ s, i }) => {
  const dark = s.stile === "scuro";
  const img = sezImg(s);
  const external = /^https?:/.test(s.pulsante_link);
  return (
    <div className={`mx-auto grid max-w-6xl gap-10 px-5 ${img ? "lg:grid-cols-2 lg:items-center" : ""}`}>
      <div>
        {s.sopratitolo && <p className={`text-xs font-bold uppercase tracking-[0.2em] ${dark ? "text-[#FBBF24]" : "text-[#D97706]"}`}>{s.sopratitolo}</p>}
        {s.titolo && <h2 data-testid={`sezione-titolo-${i}`} className={`mt-2 font-heading text-3xl font-extrabold sm:text-4xl ${dark ? "text-white" : "text-[#14382C]"}`}>{s.titolo}</h2>}
        {s.testo && <p className={`mt-4 whitespace-pre-line ${dark ? "text-emerald-50/80" : "text-slate-600"}`}>{s.testo}</p>}
        {s.pulsante_testo && s.pulsante_link && (
          <a href={s.pulsante_link} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} data-testid={`sezione-pulsante-${i}`}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#D97706] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5">{s.pulsante_testo} {external && <ExternalLink className="h-4 w-4" />}</a>
        )}
      </div>
      {img && <img src={img} alt={s.titolo || "Foto"} loading="lazy" className="w-full rounded-xl object-cover shadow-xl" data-testid={`sezione-img-${i}`} />}
      {s.voci?.length > 0 && (
        <div className="grid gap-5 sm:grid-cols-2 lg:col-span-full lg:grid-cols-3">
          {s.voci.map((v, k) => (
            <div key={k} data-testid={`sezione-${i}-voce-${k}`} className={`rounded-xl border p-6 ${dark ? "border-white/15 bg-white/5" : "border-slate-200 bg-white"}`}>
              <p className={`font-heading text-lg font-bold ${dark ? "text-white" : "text-slate-900"}`}>{v.t}</p>
              <p className={`mt-1 whitespace-pre-line text-sm ${dark ? "text-emerald-50/75" : "text-slate-600"}`}>{v.d}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AddSezioneButton = ({ onClick, label = "Aggiungi una sezione" }) => (
  <div className="mx-auto max-w-6xl px-5 pb-36 pt-8 sm:pb-8">
    <button type="button" onClick={onClick} data-testid="sezione-add" className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#D97706]/60 py-6 text-sm font-semibold text-[#D97706] transition-colors hover:bg-amber-50">
      <Plus className="h-4 w-4" /> {label}
    </button>
  </div>
);

export const SezioniPersonalizzate = ({ pagina, edit, emptyText }) => {
  const { items, reload } = useSezioni(pagina, edit);
  const publish = async (s) => {
    const pubblicata = s.pubblicata === false;
    try { await api.post(`/sito/sezioni/${s.id}/pubblica`, { pubblicata }); toast.success(pubblicata ? "Sezione pubblicata: ora è visibile a tutti" : "Sezione nascosta: torna in bozza"); reload(); } catch { toast.error("Operazione non riuscita"); }
  };
  const [editing, setEditing] = useState(undefined);
  const move = async (s, dir) => { await api.post(`/sito/sezioni/${s.id}/sposta`, { dir }); reload(); };
  const remove = async (s) => {
    if (!window.confirm(`Eliminare la sezione "${s.titolo || "senza titolo"}"?`)) { return; }
    try { await api.delete(`/sito/sezioni/${s.id}`); toast.success("Sezione eliminata"); reload(); } catch { toast.error("Eliminazione non riuscita"); }
  };
  return (
    <div data-testid={`sezioni-${pagina === "home" ? "home" : "pagina"}`}>
      {items.length === 0 && emptyText && <p className="mx-auto max-w-6xl px-5 py-16 text-slate-500" data-testid="pagina-vuota">{emptyText}</p>}
      {items.map((s, i) => (
        <section key={s.id} data-testid={`sezione-${i}`} className={`relative py-20 ${s.stile === "scuro" ? "bg-[#14382C]" : "bg-slate-50"} ${s.pubblicata === false ? "outline-dashed outline-2 -outline-offset-8 outline-amber-400" : ""}`}>
          {edit && <SezioneTools s={s} i={i} n={items.length} onEdit={() => setEditing(s)} onMove={(dir) => move(s, dir)} onDelete={() => remove(s)} onPublish={() => publish(s)} />}
          {s.pubblicata === false && <span data-testid={`sezione-bozza-${i}`} className="absolute left-4 top-4 z-20 rounded-full bg-amber-400 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-950 shadow">Bozza · non visibile al pubblico</span>}
          <div className={s.pubblicata === false ? "opacity-70" : ""}><SezioneBody s={s} i={i} /></div>
        </section>
      ))}
      {edit && <AddSezioneButton onClick={() => setEditing(null)} />}
      {editing !== undefined && <SezioneDialog sezione={editing} pagina={pagina} onClose={() => setEditing(undefined)} onSaved={reload} />}
    </div>
  );
};

const PaginaRow = ({ p, i, n, reload }) => {
  const [titolo, setTitolo] = useState(p.titolo);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onPage = pathname === `/sito/p/${p.slug}`;
  const act = async (fn, ok) => {
    try {
      const r = await fn();
      if (ok) { toast.success(ok); }
      if (onPage && r?.data?.slug && r.data.slug !== p.slug) { navigate(`/sito/p/${r.data.slug}`, { replace: true }); }
      if (onPage && r?.data?.ok) { navigate("/sito", { replace: true }); }
      reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Operazione non riuscita"); }
  };
  const remove = () => {
    if (window.confirm(`Eliminare la pagina "${p.titolo}" e tutto il suo contenuto?`)) { act(() => api.delete(`/sito/pagine/${p.id}`), "Pagina eliminata"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 p-2" data-testid={`pagina-row-${i}`}>
      <Input value={titolo} onChange={(e) => setTitolo(e.target.value)} className="min-w-0 flex-1" data-testid={`pagina-titolo-${i}`} />
      {titolo !== p.titolo && <Button size="sm" onClick={() => act(() => api.put(`/sito/pagine/${p.id}`, { titolo }), "Nome aggiornato")} data-testid={`pagina-rename-${i}`}>Salva</Button>}
      <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => act(() => api.post(`/sito/pagine/${p.id}/sposta`, { dir: -1 }))} data-testid={`pagina-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" disabled={i === n - 1} onClick={() => act(() => api.post(`/sito/pagine/${p.id}/sposta`, { dir: 1 }))} data-testid={`pagina-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
      <Link to={`/sito/p/${p.slug}`} className="text-xs font-semibold text-[#166534] hover:underline" data-testid={`pagina-open-${i}`}>Apri</Link>
      <Button size="icon" variant="ghost" className="text-red-700" onClick={remove} data-testid={`pagina-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
};

export const PagineManager = () => {
  const { pagine, reload } = usePagine();
  const [open, setOpen] = useState(false);
  const [nuova, setNuova] = useState("");
  const add = async () => {
    try { await api.post("/sito/pagine", { titolo: nuova }); toast.success("Pagina creata: ora è nel menu in alto"); setNuova(""); reload(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Creazione non riuscita"); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="pagine-manager-open"
        className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-[#14382C] shadow-2xl ring-1 ring-slate-200 transition-colors hover:bg-slate-50">
        <LayoutList className="h-4 w-4" /> Pagine del menu
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="pagine-manager">
          <DialogHeader><DialogTitle>Pagine nel menu in alto</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Le nuove pagine compaiono nel menu del sito dopo le voci fisse. Aprile per aggiungere sezioni, testi e foto.</p>
          <div className="space-y-2">
            {pagine.length === 0 && <p className="text-sm text-slate-400" data-testid="pagine-empty">Nessuna pagina aggiunta.</p>}
            {pagine.map((p, i) => <PaginaRow key={`${p.id}-${p.titolo}`} p={p} i={i} n={pagine.length} reload={reload} />)}
          </div>
          <div className="flex gap-2 border-t pt-4">
            <Input value={nuova} onChange={(e) => setNuova(e.target.value)} placeholder="Nome della nuova pagina (es. Trasparenza)" data-testid="pagina-new-titolo" />
            <Button onClick={add} disabled={!nuova.trim()} className="bg-[#D97706] hover:bg-[#b45309]" data-testid="pagina-new-add"><Plus className="mr-1 h-4 w-4" /> Crea</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
